import { model as config, remote } from '@/config'
import { combine, createEvent, createStore } from 'effector'
import { persist } from 'effector-storage/local'
import { noop } from '~/shared/effector'
import { model as session } from '~/entities/session'

export const init = createEvent()

/**
 * Store that indicates whether the pin lock feature could be enabled/disabled in settings.
 * By default it is `false`, meaning that the pin lock feature is always enabled.
 */
export const $isSettingsEnabled = config.get(
  remote.tria_isPinLockInSettingsEnabled
)

/**
 * Enable the pin lock feature for a specific subscriber
 */
export const enable = createEvent<string>()

/**
 * Disable the pin lock feature for a specific subscriber
 */
export const disable = createEvent<string>()

/**
 * Store that indicates whether the pin lock feature is enabled or disabled locally for each subscriber.
 * Uses accountNumber as keys in the object.
 * By default it is an empty object, meaning no subscribers have pin lock enabled.
 */
const $enabledInternal = createStore<Record<string, boolean>>({}) //
  .on(enable, (state, accountNumber) => ({
    ...state,
    [accountNumber]: true,
  }))
  .on(disable, (state, accountNumber) => ({
    ...state,
    [accountNumber]: false,
  }))

// Persist the enabled state of the pin lock feature locally in the browser's local storage.
//
// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $enabledInternal,
  key: 'tria__pin-lock_feature',
  fail: noop,
})

/**
 * Store that indicates whether the pin lock feature is enabled or disabled locally.
 * This is the public state of the pin lock feature, taking into account the settings.
 */
export const $enabled = combine(
  $enabledInternal,
  session.$session,
  $isSettingsEnabled,
  (enabledInternal, session, isSettingsEnabled) => {
    if (!isSettingsEnabled) return true

    const accountNumber = session?.accountNumber ?? ''

    return enabledInternal[accountNumber] ?? false
  }
)
